// ── Change Order Halt (UI-only assertion) ──
// Thin, read-only predicate that returns the active blocking Change Order
// for a job, if any. Used to replace the installer execution surface with a
// halt card so the crew cannot start/continue work while a CO is being
// negotiated.
//
// SCOPE (v1, deliberately minimal):
//   • Halt when ANY ChangeOrder.status is "Draft" or "Pending Review"
//   • No phase scoping (affectedPhases not yet on schema)
//   • No mutation of JobSpec / PO / Kit / executionReady
//   • No override path
//
// FOLLOW-UPS (do not implement here):
//   • Add ChangeOrder.affectedPhases[] to scope halt to specific phases
//   • Mark executionReady stale when signedAt > executionReady.markedAt
//
// Reuses existing primitives only — no new schema, no new workflow.

import type { Job } from "./types";
import type { ChangeOrder, ChangeOrderStatus } from "./changeOrderTypes";

const BLOCKING: readonly ChangeOrderStatus[] = ["Draft", "Pending Review"];

export interface ChangeOrderHaltVerdict {
  /** True when no blocking CO exists. */
  ok: boolean;
  /** The blocking CO (highest orderNumber wins if multiple). */
  blockingOrder?: ChangeOrder;
  /** Human-readable reason composed from the CO. */
  reason?: string;
}

/** Pick the most relevant blocking CO — newest orderNumber first. */
function pickBlocking(cos: ChangeOrder[]): ChangeOrder | undefined {
  const blocking = cos.filter((co) => BLOCKING.includes(co.status));
  if (blocking.length === 0) return undefined;
  return [...blocking].sort((a, b) => b.orderNumber - a.orderNumber)[0];
}

/** Compose a human reason from the CO (summary → line items → notes). */
function composeReason(co: ChangeOrder): string {
  const trimmed = co.summary?.trim();
  if (trimmed) return trimmed;
  const lines = co.lineItems
    ?.map((li) => li.description?.trim())
    .filter((s): s is string => !!s);
  if (lines && lines.length > 0) return lines.join(" · ");
  const notes = co.notes?.trim();
  if (notes) return notes;
  return "Change Order under review.";
}

export function assertChangeOrderHalt(job: Job): ChangeOrderHaltVerdict {
  const cos = job.changeOrders ?? [];
  const blocking = pickBlocking(cos);
  if (!blocking) return { ok: true };
  return {
    ok: false,
    blockingOrder: blocking,
    reason: composeReason(blocking),
  };
}
